export const CLUB_MEMBERSHIP_NAME = "Club";

export const CLUB_WEB_JOIN_PATH = "/join/club";

export const REFERRAL_LANDING_PATH = "/referral";

// Keep in sync with functions/src/domain/constants/clubMembership.ts (backend cannot import client).
export const CLUB_STRIPE_PRODUCT_IDS = {
  developStaging: "prod_VP20KvGJoXXwm6",
  production: "prod_VNuqf0ZqfIXMFy",
} as const;

export const CLUB_STRIPE_PRICE_IDS = {
  developStaging: "price_1UODwhEAI3qNqyTatKAU99Kf",
  production: "price_1UN90yEAI3qNqyTaNHHGXOlh",
} as const;

export const ALL_CLUB_STRIPE_PRODUCT_IDS: readonly string[] = [
  CLUB_STRIPE_PRODUCT_IDS.developStaging,
  CLUB_STRIPE_PRODUCT_IDS.production,
];

export type MembershipTrack = "club";

export const isClubStripeProductId = (
  productId: string | null | undefined
): boolean =>
  !!productId && ALL_CLUB_STRIPE_PRODUCT_IDS.includes(productId);

export const isClubMembershipName = (
  name: string | null | undefined
): boolean => (name ?? "").trim().toLowerCase() === "club";

export const isClubMembershipTrack = (
  track: string | null | undefined
): boolean => track === "club";

export const getReferAMateLandingPath = (
  membershipTrack: string | null | undefined
): string =>
  isClubMembershipTrack(membershipTrack)
    ? CLUB_WEB_JOIN_PATH
    : REFERRAL_LANDING_PATH;

export const isClubPlan = (plan: {
  docId?: string;
  name?: string;
}): boolean => isClubStripeProductId(plan.docId) || isClubMembershipName(plan.name);

export const resolveClubTrackTierMode = (
  membershipTrack: string | null | undefined,
  subscription: { docId?: string; name?: string }
): { handled: boolean; mode?: "currentMembership" } => {
  if (!isClubMembershipTrack(membershipTrack)) {
    return { handled: false };
  }
  return isClubPlan(subscription)
    ? { handled: true, mode: "currentMembership" }
    : { handled: true, mode: undefined };
};

export const resolveEffectiveMembershipDocId = (params: {
  currentDocId: string;
  membershipTrack: string | null | undefined;
  clubDocId: string | undefined;
}): string => {
  if (
    isClubMembershipTrack(params.membershipTrack) &&
    params.clubDocId !== undefined
  ) {
    return params.clubDocId;
  }
  return params.currentDocId;
};

export const filterPlansForMembershipTrack = <
  T extends { docId: string; name: string; isOneOff?: boolean },
>(
  plans: T[],
  membershipTrack: string | null | undefined
): T[] => {
  const isClubTrack = isClubMembershipTrack(membershipTrack);
  return plans.filter((plan) => {
    if (plan.isOneOff) {
      return true;
    }
    return isClubTrack ? isClubPlan(plan) : !isClubPlan(plan);
  });
};

export const resolveJoinPlanFromList = <T extends { name: string }>(
  plans: T[],
  subscriptionName: string
): T | undefined => {
  const key = subscriptionName.toLowerCase().trim();
  const matchedByName = plans.find(
    (sub) => sub.name.toLowerCase().trim() === key
  );
  if (key === "club") {
    return matchedByName;
  }
  return matchedByName ?? plans[1];
};
